import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "utils/api";
import { toast } from "react-hot-toast";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import {
  MdArrowBack,
  MdPerson,
  MdLocalLaundryService,
  MdDeliveryDining,
  MdSchedule,
  MdLocationOn,
  MdPayment,
  MdShoppingBag,
  MdTimeline,
} from "react-icons/md";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (!positions || positions.length === 0) return;
    if (positions.length === 1) {
      map.setView(positions[0], 14);
      return;
    }
    const bounds = L.latLngBounds(positions);
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 14 });
  }, [map, positions]);
  return null;
}

const statusStyles = {
  "Pending": "bg-yellow-100 text-yellow-800 border-yellow-300",    // yellow for pending, cautionary
  "Confirmed": "bg-green-100 text-green-800 border-green-300",    // green for confirmed, positive
  "Assigned": "bg-blue-100 text-blue-800 border-blue-300",        // blue for assignment, neutral but active
  "Picked Up": "bg-teal-100 text-teal-800 border-teal-300",      // teal for progress, calm transition
  "In Processing": "bg-purple-100 text-purple-800 border-purple-300", // purple for in-progress, complex task
  "Out for Delivery": "bg-light-blue-100 text-light-blue-800 border-light-blue-300", // light blue, action
  "Delivered": "bg-green-100 text-green-800 border-green-300",    // green for success, completion
  "Cancelled by Customer": "bg-orange-100 text-orange-800 border-orange-300", // orange for customer cancellations
  "Cancelled": "bg-red-100 text-red-800 border-red-300",          // red for cancellations, strong signal
  "Refund Processing": "bg-pink-100 text-pink-800 border-pink-300", // pink for refunds, financial aspect
  "Refunded": "bg-gray-100 text-gray-600 border-gray-300",        // gray for refunded, neutral, final
};
const InfoCard = ({ icon: Icon, label, value, className = "" }) => (
  <div className={`rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md ${className}`}>
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-600">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
        <p className="truncate text-sm font-semibold text-gray-900">{value || "—"}</p>
      </div>
    </div>
  </div>
);

const SectionCard = ({ title, icon: Icon, children }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
    <div className="mb-4 flex items-center gap-2 border-b border-gray-100 pb-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-base font-bold text-gray-900">{title}</h3>
    </div>
    {children}
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2.5 text-sm">
    <span className="font-medium text-gray-500">{label}</span>
    <span className="text-right font-medium text-gray-900">{value ?? "—"}</span>
  </div>
);

export default function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [runners, setRunners] = useState([]);
  const [runnerLocation, setRunnerLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [transitions, setTransitions] = useState({});
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [newRunner, setNewRunner] = useState("");
  const [statusNotes, setStatusNotes] = useState("");

  const runnerMap = new Map(runners.map((r) => [r.id, r.name]));

  const fetchOrder = async () => {
    try {
      const orderRes = await api.get(`/order/${id}`);
      setOrder(orderRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get(`/admin/order/${id}/history`);
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orderRes, runnersRes, statusesRes] = await Promise.all([
          api.get(`/order/${id}`),
          api.post("/list_user", { role: "R" }).catch(() => ({ data: [] })),
          api.get("/admin/order/statuses").catch(() => ({ data: {} })),
        ]);
        setOrder(orderRes.data);
        setRunners(runnersRes.data || []);
        setTransitions(statusesRes.data?.transitions || {});

        const payload = { order_id: id };
        const transactionRes = await api.post("/transaction/", payload).catch(() => ({ data: [] }));
        if (transactionRes.data?.length > 0) {
          setTransaction(transactionRes.data[0]);
        }

        try {
          const histRes = await api.get(`/admin/order/${id}/history`);
          setHistory(histRes.data || []);
        } catch { setHistory([]); }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    setUpdatingStatus(true);
    try {
      const payload = { status: newStatus, notes: statusNotes || undefined };
      if (newRunner) payload.runner_id = newRunner;
      await api.post(`/admin/order/${id}/update_status`, payload);
      toast.success(`Status updated to ${newStatus}`);
      setShowStatusModal(false);
      setNewStatus("");
      setNewRunner("");
      setStatusNotes("");
      await Promise.all([fetchOrder(), fetchHistory()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Poll + WebSocket for runner live location when order has an assigned runner
  useEffect(() => {
    if (!order?.assigned_to) {
      setRunnerLocation(null);
      return;
    }
    const fetchRunnerLocation = async () => {
      try {
        const res = await api.get(`/admin/runner/${order.assigned_to}/location`);
        if (res.data?.latitude != null && res.data?.longitude != null) {
          setRunnerLocation({
            lat: res.data.latitude,
            lon: res.data.longitude,
            last_updated: res.data.last_updated,
          });
        } else {
          setRunnerLocation(null);
        }
      } catch {
        setRunnerLocation(null);
      }
    };
    fetchRunnerLocation();
    const interval = setInterval(fetchRunnerLocation, 10000);

    const base = (process.env.REACT_APP_API_BASE_URL || "http://localhost:8000").replace(/^https?/, (m) => (m === "https" ? "wss" : "ws"));
    const token = localStorage.getItem("token");
    let ws = null;
    if (token) {
      try {
        ws = new WebSocket(`${base}/ws/admin?token=${encodeURIComponent(token)}`);
        ws.onopen = () => {
          ws.send(JSON.stringify({ type: "watch_runner", runner_id: order.assigned_to }));
        };
        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data?.type === "runner_location" && data.runner_id === order.assigned_to && data.lat != null && data.lon != null) {
              setRunnerLocation({ lat: data.lat, lon: data.lon, last_updated: data.last_updated });
            }
          } catch {}
        };
      } catch {}
    }

    return () => {
      clearInterval(interval);
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [order?.assigned_to]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          <p className="text-sm font-medium text-gray-500">Loading booking...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-center">
          <p className="font-medium text-red-800">Order not found.</p>
          <button
            onClick={() => navigate("/admin/bookings")}
            className="mt-3 text-sm font-medium text-red-600 underline hover:no-underline"
          >
            Back to Bookings
          </button>
        </div>
      </div>
    );
  }

  const orderRef = order.ref_id || `#${order.id}`;
  const statusClass = statusStyles[order.status] || "bg-gray-100 text-gray-800 border-gray-200";
  const totalQty = order.booking_service_details?.reduce((sum, item) => sum + (item.product_count || 0), 0) ?? 0;
  const allowedNext = transitions[order.status] || [];

  const pickupPos =
    order.pickup_lat != null && order.pickup_lon != null
      ? [Number(order.pickup_lat), Number(order.pickup_lon)]
      : null;
  const dropPos =
    order.drop_lat != null && order.drop_lon != null
      ? [Number(order.drop_lat), Number(order.drop_lon)]
      : null;
  const sameLocation =
    pickupPos &&
    dropPos &&
    Math.abs(pickupPos[0] - dropPos[0]) < 1e-5 &&
    Math.abs(pickupPos[1] - dropPos[1]) < 1e-5;
  const runnerPos = runnerLocation?.lat != null && runnerLocation?.lon != null
    ? [Number(runnerLocation.lat), Number(runnerLocation.lon)]
    : null;
  const mapPositions = (() => {
    let positions = [];
    if (sameLocation || (pickupPos && !dropPos) || (!pickupPos && dropPos)) {
      const single = pickupPos || dropPos;
      positions = single ? [single] : [];
    } else {
      positions = [pickupPos, dropPos].filter(Boolean);
    }
    if (runnerPos) positions.push(runnerPos);
    return positions;
  })();
  const defaultCenter = [12.9716, 77.5946];
  const mapCenter = mapPositions.length > 0 ? mapPositions[0] : defaultCenter;

  return (
    <div className="min-w-0 space-y-6 p-4 sm:p-6">
      {/* Header: Back + Title + Status + Update */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/bookings")}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
            aria-label="Back to Bookings"
          >
            <MdArrowBack className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Booking Details</h1>
            <p className="text-sm text-gray-500">{orderRef}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${statusClass}`}>
            {order.status}
          </span>
          {allowedNext.length > 0 && (
            <button
              onClick={() => setShowStatusModal(true)}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
            >
              Update Status
            </button>
          )}
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500/50">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold mb-4">Update Order Status</h3>
            <p className="text-sm text-gray-500 mb-3">Current: <span className="font-semibold text-gray-800">{order.status}</span></p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">New Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">Select status...</option>
                  {allowedNext.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              {(newStatus === "Assigned" || newStatus === "Out for Delivery") && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                    {newStatus === "Out for Delivery" ? "Delivery Runner (optional)" : "Assign Runner"}
                  </label>
                  <select
                    value={newRunner}
                    onChange={(e) => setNewRunner(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="">{order.assigned_to ? `Keep current (${runnerMap.get(order.assigned_to) || "current"})` : "Select runner..."}</option>
                    {runners.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Notes (optional)</label>
                <textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  rows={2}
                  placeholder="Reason for status change..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => { setShowStatusModal(false); setNewStatus(""); setNewRunner(""); setStatusNotes(""); }}
                className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateStatus}
                disabled={!newStatus || updatingStatus}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${!newStatus || updatingStatus ? "bg-gray-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
              >
                {updatingStatus ? "Updating..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <InfoCard label="Customer" value={order.users_details?.name} icon={MdPerson} />
        <InfoCard
          label="Service"
          value={order.booking_service_details?.[0]?.service_details?.name || order.booking_service_details?.[0]?.service_details?.service_name}
          icon={MdLocalLaundryService}
        />
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-600">
              <MdDeliveryDining className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Runner</p>
              {order.assigned_to ? (
                <Link
                  to={`/admin/runner_details/${order.assigned_to}`}
                  className="truncate block text-sm font-semibold text-blue-600 underline decoration-blue-600/50 hover:decoration-blue-600 hover:text-blue-700"
                >
                  {runnerMap.get(order.assigned_to) || `Runner #${order.assigned_to}`}
                </Link>
              ) : (
                <p className="truncate text-sm font-semibold text-gray-500">Not assigned</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Two columns: Booking + Payment */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Booking schedule & addresses */}
        <SectionCard title="Schedule & Locations" icon={MdSchedule}>
          <div className="space-y-1">
            <DetailRow label="Pickup" value={`${order.pickup_date} · ${order.pickup_time}`} />
            <div className="flex justify-between gap-4 py-2.5 text-sm">
              <span className="font-medium text-gray-500">Pickup address</span>
              <span className="max-w-[60%] text-right text-gray-900">{order.pickup_location || "—"}</span>
            </div>
            <DetailRow label="Drop" value={`${order.drop_date} · ${order.drop_time}`} />
            <div className="flex justify-between gap-4 py-2.5 text-sm">
              <span className="font-medium text-gray-500">Drop address</span>
              <span className="max-w-[60%] text-right text-gray-900">{order.drop_location || "—"}</span>
            </div>
          </div>
        </SectionCard>

        {/* Payment */}
        <SectionCard title="Payment" icon={MdPayment}>
          {transaction ? (
            <div className="space-y-1">
              <DetailRow label="Bill amount" value={transaction.bill_amount != null ? `₹${Number(transaction.bill_amount).toLocaleString("en-IN")}` : "—"} />
              <DetailRow label="Payment date" value={transaction.payment_date} />
              <DetailRow label="Payment mode" value={transaction.payment_mode === 1 ? "Credit Card" : "Other"} />
              <div className="flex justify-between gap-4 pt-2">
                <span className="font-medium text-gray-500">Status</span>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    transaction.status === 0 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
                  }`}
                >
                  {transaction.status === 0 ? "Pending" : "Completed"}
                </span>
              </div>
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-gray-500">No payment details for this order.</p>
          )}
        </SectionCard>
      </div>

      {/* Items */}
      <SectionCard title="Items" icon={MdShoppingBag}>
        {order.booking_service_details?.length > 0 ? (
          <ul className="space-y-2">
            {order.booking_service_details.map((item, i) => (
              <li
                key={i}
                className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2.5 text-sm"
              >
                <span className="font-medium text-gray-900">{item.product_details?.product_name || "Item"}</span>
                <span className="rounded-full bg-blue-100 px-2.5 py-0.5 font-semibold text-blue-800">
                  ×{item.product_count ?? 0}
                </span>
              </li>
            ))}
            <li className="flex justify-between border-t border-gray-100 pt-2 text-sm font-semibold text-gray-700">
              <span>Total items</span>
              <span>{totalQty}</span>
            </li>
          </ul>
        ) : (
          <p className="py-4 text-center text-sm text-gray-500">No items in this booking.</p>
        )}
      </SectionCard>

      {/* Status History Timeline */}
      {history.length > 0 && (
        <SectionCard title="Order History" icon={MdTimeline}>
          <div className="relative">
            <div className="absolute left-[15px] top-0 h-full w-0.5 bg-gray-200" />
            <div className="space-y-4">
              {history.map((h, i) => (
                <div key={h.id || i} className="relative flex items-start gap-4 pl-9">
                  <div className={`absolute left-[10px] top-1.5 h-3 w-3 rounded-full border-2 border-white shadow-sm ${
                    h.to_status === "Delivered" ? "bg-emerald-500"
                    : h.to_status?.startsWith("Cancel") || h.to_status?.startsWith("Refund") ? "bg-red-500"
                    : "bg-blue-500"
                  }`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${statusStyles[h.to_status] || "bg-gray-100 text-gray-800 border-gray-200"}`}>
                        {h.to_status}
                      </span>
                      {h.from_status && h.from_status !== h.to_status && (
                        <span className="text-xs text-gray-400">from {h.from_status}</span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      {h.changed_by_name && (
                        <span>by <span className="font-medium text-gray-700">{h.changed_by_name}</span> ({h.changed_by_role === "R" ? "Runner" : "Admin"})</span>
                      )}
                      {h.runner_name && h.runner_id !== h.changed_by && (
                        <span>· Runner: <span className="font-medium text-gray-700">{h.runner_name}</span></span>
                      )}
                      {h.created_at && (
                        <span>· {new Date(h.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                      )}
                    </div>
                    {h.notes && <p className="mt-1 text-xs text-gray-600 italic">{h.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
      )}

      {/* Map: Pickup & Drop locations */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/80 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MdLocationOn className="h-4 w-4 text-blue-500" />
            <span className="font-medium">
              {mapPositions.length === 0
                ? "Location"
                : "Pickup, Drop" + (runnerPos ? " & Runner (live)" : "")}
            </span>
          </div>
          {order.assigned_to && !runnerPos && (
            <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-1 rounded">
              Runner assigned; location appears when runner app sends updates
            </span>
          )}
          {runnerPos && runnerLocation?.last_updated && (
            <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded">
              Runner live · {new Date(runnerLocation.last_updated).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
        </div>
        <div className="relative h-[380px] w-full sm:h-[420px]">
          {mapPositions.length === 0 && (
            <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-gray-50/95 rounded-b-2xl">
              <p className="text-sm font-medium text-gray-600 text-center px-4">
                Location coordinates not available for this order. Add pickup/drop lat & long when creating the booking to see the map.
              </p>
            </div>
          )}
          <MapContainer center={mapCenter} zoom={mapPositions.length ? 13 : 10} style={{ height: "100%", width: "100%" }} className="rounded-b-2xl">
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; OpenStreetMap &copy; CartoDB'
            />
            {sameLocation && pickupPos && (
              <Marker position={pickupPos}>
                <Popup>Pickup & Drop (same location)</Popup>
              </Marker>
            )}
            {!sameLocation && pickupPos && (
              <Marker position={pickupPos}>
                <Popup>Pickup</Popup>
              </Marker>
            )}
            {!sameLocation && dropPos && (
              <Marker position={dropPos}>
                <Popup>Drop</Popup>
              </Marker>
            )}
            {runnerPos && (
              <Marker position={runnerPos}>
                <Popup>Runner (live location)</Popup>
              </Marker>
            )}
            {mapPositions.length > 0 && <FitBounds positions={mapPositions} />}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}
